import React, { useState, useEffect, useCallback } from 'react';
import { filterInput } from '../../utils/inputSanitization';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../../components/common/ConfirmDialog';
import PolicyGeneratorLink from '../../components/common/PolicyGeneratorLink';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Switch,
  FormControlLabel,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as BackIcon,
  AccessTime as TimeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Policy as PolicyIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  People as PeopleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  createJITRequest,
  getAvailableAccounts,
  getAvailablePermissionSets,
  getAWSManagedPolicies,
  validateIAMPolicy,
} from '../../services/jitApi';
import { formatPolicyJson, previewPolicyJson } from '../../utils/policyJson';

// Duration options from 1 hour to 1 month
const durationOptions = [
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '1 day' },
  { value: 48, label: '2 days' },
  { value: 72, label: '3 days' },
  { value: 168, label: '1 week' },
  { value: 336, label: '2 weeks' },
  { value: 720, label: '1 month' },
];

// Session duration options for custom permission sets
const sessionDurationOptions = [
  { value: 'PT1H', label: '1 hour' },
  { value: 'PT2H', label: '2 hours' },
  { value: 'PT4H', label: '4 hours' },
  { value: 'PT8H', label: '8 hours' },
  { value: 'PT12H', label: '12 hours' },
];

// Helper function to count non-empty inline policies
const countInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return 0;
  return Object.values(inlinePolicies).filter(policy => policy && policy.trim() !== '').length;
};

const JITRequestForm = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [permissionSets, setPermissionSets] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPermissionSet, setSelectedPermissionSet] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(durationOptions[2]); // Default 4 hours
  const [justification, setJustification] = useState('');

  // Custom permission set fields
  const [useCustomPermissionSet, setUseCustomPermissionSet] = useState(false);
  const [customPSName, setCustomPSName] = useState('');
  const [customSessionDuration, setCustomSessionDuration] = useState(sessionDurationOptions[0]);

  // Managed policies state (matching PermissionSetForm style)
  const [awsManagedPolicies, setAwsManagedPolicies] = useState([]);
  const [selectedManagedPolicies, setSelectedManagedPolicies] = useState([]);
  const [policySearchTerm, setPolicySearchTerm] = useState('');
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState('');
  const [policySortBy, setPolicySortBy] = useState('name');
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [policiesPerPage] = useState(10);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  // Inline policies state (matching PermissionSetForm style)
  const [inlinePolicies, setInlinePolicies] = useState({});
  const [inlinePolicyName, setInlinePolicyName] = useState('');
  const [inlinePolicyContent, setInlinePolicyContent] = useState('');
  const [policyValidation, setPolicyValidation] = useState({
    isValidating: false,
    result: null,
    error: null,
  });

  const [formErrors, setFormErrors] = useState({});

  // State for viewing inline policy details
  const [viewPolicyDialog, setViewPolicyDialog] = useState({
    open: false,
    policyName: '',
    policyContent: '',
  });

  // State for success dialog showing approvers
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    approvers: [],
    isCustomPermissionSet: false,
  });

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountsRes, permSetsRes] = await Promise.all([
        getAvailableAccounts(),
        getAvailablePermissionSets(),
      ]);

      setAccounts(accountsRes.data || []);
      setPermissionSets(permSetsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch form data:', err);
      setError('Failed to load available accounts and permission sets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch AWS managed policies for the policy picker
  const fetchAwsPolicies = async () => {
    setIsLoadingPolicies(true);
    try {
      const response = await getAWSManagedPolicies(true);
      setAwsManagedPolicies(response.data || []);
      return response.data || [];
    } catch (err) {
      console.error('Failed to fetch AWS managed policies:', err);
      toast.error('Failed to load AWS managed policies');
      return [];
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  // Debounced policy validation
  const debouncedValidatePolicy = useCallback(
    debounce(async (policyContent) => {
      if (!policyContent || policyContent.trim() === '') {
        setPolicyValidation({ isValidating: false, result: null, error: null });
        return;
      }

      // First check if it's valid JSON
      try {
        JSON.parse(policyContent);
      } catch (error) {
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: 'Invalid JSON format. Please check the syntax.',
        });
        return;
      }

      setPolicyValidation({ isValidating: true, result: null, error: null });

      try {
        const response = await validateIAMPolicy(policyContent);
        if (response.success) {
          setPolicyValidation({
            isValidating: false,
            result: response.data,
            error: null,
          });
        } else {
          setPolicyValidation({
            isValidating: false,
            result: null,
            error: response.message || 'Failed to validate policy',
          });
        }
      } catch (error) {
        console.error('Policy validation error:', error);
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: error.message || 'An error occurred during policy validation',
        });
      }
    }, 800),
    []
  );

  useEffect(() => {
    if (inlinePolicyContent) {
      debouncedValidatePolicy(inlinePolicyContent);
    }
    return () => {
      debouncedValidatePolicy.cancel();
    };
  }, [inlinePolicyContent, debouncedValidatePolicy]);

  // Policy category helpers
  const getPolicyCategories = () => {
    const categories = new Set();
    (awsManagedPolicies || []).forEach(policy => {
      if (policy.name.includes('Amazon')) categories.add('Amazon Services');
      else if (policy.name.includes('AWS')) categories.add('AWS Services');
      else if (policy.name.includes('IAM') || policy.name.includes('Security')) categories.add('Security & IAM');
      else if (policy.name.includes('ReadOnly') || policy.name.includes('Read')) categories.add('Read Only');
      else if (policy.name.includes('Admin') || policy.name.includes('Full')) categories.add('Administrative');
      else if (policy.name.includes('Power') || policy.name.includes('Developer')) categories.add('Developer');
      else categories.add('Other');
    });
    return Array.from(categories).sort();
  };

  const getFilteredAndSortedPolicies = () => {
    let filtered = (awsManagedPolicies || []).filter(policy => {
      const matchesSearch = !policySearchTerm ||
        policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
        (policy.description && policy.description.toLowerCase().includes(policySearchTerm.toLowerCase())) ||
        policy.arn.toLowerCase().includes(policySearchTerm.toLowerCase());

      const matchesCategory = !selectedPolicyCategory ||
        (selectedPolicyCategory === 'Amazon Services' && policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'AWS Services' && policy.name.includes('AWS') && !policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'Security & IAM' && (policy.name.includes('IAM') || policy.name.includes('Security'))) ||
        (selectedPolicyCategory === 'Read Only' && (policy.name.includes('ReadOnly') || policy.name.includes('Read'))) ||
        (selectedPolicyCategory === 'Administrative' && (policy.name.includes('Admin') || policy.name.includes('Full'))) ||
        (selectedPolicyCategory === 'Developer' && (policy.name.includes('Power') || policy.name.includes('Developer'))) ||
        (selectedPolicyCategory === 'Other' && !policy.name.includes('Amazon') && !policy.name.includes('AWS') &&
          !policy.name.includes('IAM') && !policy.name.includes('Security') && !policy.name.includes('ReadOnly') &&
          !policy.name.includes('Read') && !policy.name.includes('Admin') && !policy.name.includes('Full') &&
          !policy.name.includes('Power') && !policy.name.includes('Developer'));

      return matchesSearch && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (policySortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'service':
          return (a.name.split(/[A-Z]/)[0] || '').localeCompare(b.name.split(/[A-Z]/)[0] || '');
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  };

  const filteredPolicies = getFilteredAndSortedPolicies();
  const totalPages = Math.ceil(filteredPolicies.length / policiesPerPage);
  const paginatedPolicies = filteredPolicies.slice(
    (currentPage - 1) * policiesPerPage,
    currentPage * policiesPerPage
  );

  // Managed policy handlers
  const handleAddManagedPolicy = (policyArn) => {
    if (selectedManagedPolicies.includes(policyArn)) return;
    setSelectedManagedPolicies([...selectedManagedPolicies, policyArn]);
  };

  const handleRemoveManagedPolicy = (policyArn) => {
    setSelectedManagedPolicies(selectedManagedPolicies.filter(arn => arn !== policyArn));
  };

  const handleShowPolicyPicker = async () => {
    if (!showPolicyPicker) {
      if (awsManagedPolicies.length === 0) {
        await fetchAwsPolicies();
      }
    }
    setShowPolicyPicker(!showPolicyPicker);
  };

  // Inline policy handlers
  const handleAddInlinePolicy = async () => {
    if (!inlinePolicyName || !inlinePolicyContent.trim()) return;

    try {
      JSON.parse(inlinePolicyContent);

      if (policyValidation.error || (policyValidation.result && policyValidation.result.findings &&
        policyValidation.result.findings.some(f => f.findingType === 'ERROR'))) {
        const confirmed = await confirm({
          title: 'Policy Validation Errors',
          message: 'The policy has validation errors. Are you sure you want to add it anyway?',
          confirmText: 'Add Anyway',
          variant: 'warning',
        });
        if (!confirmed) {
          return;
        }
      }

      setInlinePolicies({
        ...inlinePolicies,
        [inlinePolicyName]: inlinePolicyContent,
      });

      setInlinePolicyName('');
      setInlinePolicyContent('');
      setPolicyValidation({ isValidating: false, result: null, error: null });
    } catch (error) {
      toast.error('Invalid JSON format for inline policy');
    }
  };

  const handleRemoveInlinePolicy = (policyName) => {
    const newInlinePolicies = { ...inlinePolicies };
    delete newInlinePolicies[policyName];
    setInlinePolicies(newInlinePolicies);
  };

  // Validation results renderer
  const renderValidationResults = () => {
    if (policyValidation.isValidating) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#F39C12' }}>
          <CircularProgress size={14} sx={{ color: '#F39C12' }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Validating policy...
          </Typography>
        </Box>
      );
    }

    if (policyValidation.error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#E74C3C' }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            {policyValidation.error}
          </Typography>
        </Box>
      );
    }

    if (!policyValidation.result) {
      return null;
    }

    const findings = policyValidation.result.findings || [];

    if (findings.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#27AE60' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy is valid
          </Typography>
        </Box>
      );
    }

    const errors = findings.filter(f => f.findingType === 'ERROR');
    const warnings = findings.filter(f => f.findingType === 'WARNING');
    const suggestions = findings.filter(f => f.findingType === 'SUGGESTION');

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#F39C12' }}>
          <WarningIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy validation found issues
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FDF2F2', borderRadius: '6px', border: '1px solid #E74C3C20' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#E74C3C', mb: 0.5 }}>
              Errors ({errors.length})
            </Typography>
            {errors.map((error, index) => (
              <Typography key={`error-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{error.issueCode || 'Error'}:</strong> {error.findingDetails || 'Invalid policy'}
              </Typography>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FEF9E7', borderRadius: '6px', border: '1px solid #F39C1220' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#F39C12', mb: 0.5 }}>
              Warnings ({warnings.length})
            </Typography>
            {warnings.map((warning, index) => (
              <Typography key={`warning-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{warning.issueCode || 'Warning'}:</strong> {warning.findingDetails || 'Policy warning'}
              </Typography>
            ))}
          </Box>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ p: 1.5, backgroundColor: '#EBF5FB', borderRadius: '6px', border: '1px solid #4698D320' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#4698D3', mb: 0.5 }}>
              Suggestions ({suggestions.length})
            </Typography>
            {suggestions.map((suggestion, index) => (
              <Typography key={`suggestion-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{suggestion.issueCode || 'Suggestion'}:</strong> {suggestion.findingDetails || 'Policy suggestion'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const validateForm = () => {
    const errors = {};

    if (!selectedAccount) {
      errors.account = 'Please select an AWS account';
    }

    if (useCustomPermissionSet) {
      if (!customPSName.trim()) {
        errors.customPSName = 'Please provide a name for the custom permission set';
      }

      if (selectedManagedPolicies.length === 0 && countInlinePolicies(inlinePolicies) === 0) {
        errors.customPolicies = 'Please add at least one managed policy or inline policy';
      }
    } else {
      if (!selectedPermissionSet) {
        errors.permissionSet = 'Please select a permission set';
      }
    }

    if (!justification.trim()) {
      errors.justification = 'Please provide a justification for this request';
    } else if (justification.trim().length < 10) {
      errors.justification = 'Justification must be at least 10 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const requestData = {
        account_id: selectedAccount.account_id,
        requested_duration: selectedDuration.value,
        justification: justification.trim(),
        is_custom_permission_set: useCustomPermissionSet,
      };

      if (useCustomPermissionSet) {
        requestData.custom_permission_set_name = customPSName.trim();
        requestData.custom_managed_policies = selectedManagedPolicies;
        requestData.custom_session_duration = customSessionDuration.value;

        if (countInlinePolicies(inlinePolicies) > 0) {
          requestData.custom_inline_policies = inlinePolicies;
        }
      } else {
        requestData.permission_set_id = selectedPermissionSet.id;
      }

      const response = await createJITRequest(requestData);

      // Check if there are approvers to show
      const approvers = response?.data?.approvers || [];

      if (approvers.length > 0) {
        // Show success dialog with approvers
        setSuccessDialog({
          open: true,
          approvers: approvers,
          isCustomPermissionSet: useCustomPermissionSet,
        });
      } else {
        // No approvers info, just navigate
        toast.success(useCustomPermissionSet
          ? 'Custom permission set request submitted - requires multi-level approval'
          : 'Access request submitted successfully'
        );
        navigate('/my-requests');
      }
    } catch (err) {
      console.error('Failed to submit request:', err);
      const errorMessage = err.message || 'Failed to submit access request';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDurationDisplay = (hours) => {
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    if (hours < 168) return `${Math.floor(hours / 24)} day${hours >= 48 ? 's' : ''}`;
    if (hours < 720) return `${Math.floor(hours / 168)} week${hours >= 336 ? 's' : ''}`;
    return '1 month';
  };

  // Open inline policy viewer dialog
  const handleViewInlinePolicy = (policyName, policyContent) => {
    setViewPolicyDialog({
      open: true,
      policyName,
      policyContent: formatPolicyJson(policyContent),
    });
  };

  // Close inline policy viewer dialog
  const handleClosePolicyDialog = () => {
    setViewPolicyDialog({
      open: false,
      policyName: '',
      policyContent: '',
    });
  };

  // Copy policy content to clipboard
  const handleCopyPolicy = () => {
    navigator.clipboard.writeText(viewPolicyDialog.policyContent);
    toast.success('Policy copied to clipboard');
  };

  // Close success dialog and navigate to my-requests
  const handleCloseSuccessDialog = () => {
    setSuccessDialog({ open: false, approvers: [], isCustomPermissionSet: false });
    navigate('/my-requests');
  };

  // Input style matching PermissionSetForm
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': { borderColor: '#E5E5E5' },
      '&:hover fieldset': { borderColor: '#4698D3' },
      '&.Mui-focused fieldset': { borderColor: '#4698D3' },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': { color: '#4698D3' },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            textTransform: 'none',
            mb: 2,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          Back to Dashboard
        </Button>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            mb: 1,
          }}
        >
          Request Access
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}
        >
          Submit a request for temporary elevated access to an AWS account
        </Typography>
      </Box>

      </motion.div>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          maxWidth: 800,
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* AWS Account Selection */}
          <Autocomplete
            options={accounts}
            value={selectedAccount}
            onChange={(_, newValue) => {
              setSelectedAccount(newValue);
              if (formErrors.account) setFormErrors(prev => ({ ...prev, account: null }));
            }}
            getOptionLabel={(option) => `${option.name || option.account_id} (${option.account_id})`}
            isOptionEqualToValue={(option, value) => option.account_id === value?.account_id}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}>
                    {option.name || option.account_id}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                    {option.account_id}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="AWS Account"
                placeholder="Search accounts..."
                error={!!formErrors.account}
                helperText={formErrors.account}
                sx={inputStyle}
              />
            )}
            sx={{ mb: 3 }}
          />

          {/* Toggle for Custom Permission Set */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomPermissionSet}
                  onChange={(e) => {
                    setUseCustomPermissionSet(e.target.checked);
                    setFormErrors(prev => ({
                      ...prev,
                      permissionSet: null,
                      customPSName: null,
                      customPolicies: null,
                    }));
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4698D3' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4698D3' },
                  }}
                />
              }
              label={
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                  Create custom permission set
                </Typography>
              }
            />
            <Typography variant="caption" sx={{ display: 'block', color: '#666666', mt: 0.5, ml: 6 }}>
              Define a custom permission set with specific AWS managed or inline policies
            </Typography>
          </Box>

          {/* Custom Permission Set Warning */}
          {useCustomPermissionSet && (
            <Alert
              severity="info"
              icon={<WarningIcon />}
              sx={{
                mb: 3,
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                '& .MuiAlert-icon': { color: '#FF9800' },
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                <strong>Multi-level approval required:</strong> Custom permission set requests require approval from the account owner first, then from an SSO administrator.
              </Typography>
            </Alert>
          )}

          {/* Existing Permission Set Selection */}
          {!useCustomPermissionSet && (
            <>
              <Autocomplete
                options={permissionSets}
                value={selectedPermissionSet}
                onChange={(_, newValue) => {
                  setSelectedPermissionSet(newValue);
                  if (formErrors.permissionSet) setFormErrors(prev => ({ ...prev, permissionSet: null }));
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => {
                  const managedPolicies = option.managed_policies || option.managedPolicies || [];
                  const inlinePolicies = option.inline_policies || option.inlinePolicies || {};
                  const inlinePolicyCount = Object.keys(inlinePolicies).filter(k => inlinePolicies[k] && inlinePolicies[k].trim() !== '').length;
                  return (
                    <Box component="li" {...props}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}>
                            {option.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {managedPolicies.length > 0 && (
                              <Chip
                                label={`${managedPolicies.length} managed`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'Metropolis, sans-serif', backgroundColor: '#EBF5FB', color: '#4698D3' }}
                              />
                            )}
                            {inlinePolicyCount > 0 && (
                              <Chip
                                label={`${inlinePolicyCount} inline`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'Metropolis, sans-serif', backgroundColor: '#FEF9E7', color: '#F39C12' }}
                              />
                            )}
                          </Box>
                        </Box>
                        {option.description && (
                          <Typography variant="caption" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Permission Set"
                    placeholder="Search permission sets..."
                    error={!!formErrors.permissionSet}
                    helperText={formErrors.permissionSet}
                    sx={inputStyle}
                  />
                )}
                sx={{ mb: selectedPermissionSet ? 2 : 3 }}
              />

              {/* Display attached policies for selected permission set */}
              {selectedPermissionSet && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: '#F9FBFD',
                    border: '1px solid #E5E5E5',
                    borderRadius: '8px',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', mb: 2 }}
                  >
                    Attached Policies for "{selectedPermissionSet.name}"
                  </Typography>

                  {/* Managed Policies */}
                  {(() => {
                    const managedPolicies = selectedPermissionSet.managed_policies || selectedPermissionSet.managedPolicies || [];
                    if (managedPolicies.length > 0) {
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PolicyIcon sx={{ color: '#4698D3', fontSize: 18 }} />
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#253E66' }}>
                              AWS Managed Policies ({managedPolicies.length})
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 3.5 }}>
                            {managedPolicies.map((policyArn, index) => {
                              // Extract policy name from ARN
                              const policyName = policyArn.split('/').pop() || policyArn;
                              return (
                                <Chip
                                  key={index}
                                  label={policyName}
                                  size="small"
                                  sx={{
                                    fontFamily: 'Metropolis, sans-serif',
                                    fontSize: '0.75rem',
                                    backgroundColor: '#EBF5FB',
                                    color: '#4698D3',
                                    border: '1px solid #D6EAF8',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  {/* Inline Policies */}
                  {(() => {
                    const inlinePolicies = selectedPermissionSet.inline_policies || selectedPermissionSet.inlinePolicies || {};
                    const inlinePolicyEntries = Object.entries(inlinePolicies).filter(([, content]) => content && content.trim() !== '');
                    if (inlinePolicyEntries.length > 0) {
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CodeIcon sx={{ color: '#F39C12', fontSize: 18 }} />
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#253E66' }}>
                              Inline Policies ({inlinePolicyEntries.length})
                            </Typography>
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999', fontStyle: 'italic' }}>
                              Click to view policy details
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 3.5 }}>
                            {inlinePolicyEntries.map(([policyName, policyContent], index) => (
                              <Tooltip key={index} title="Click to view full policy" arrow>
                                <Chip
                                  label={policyName}
                                  size="small"
                                  icon={<ViewIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => handleViewInlinePolicy(policyName, policyContent)}
                                  sx={{
                                    fontFamily: 'Metropolis, sans-serif',
                                    fontSize: '0.75rem',
                                    backgroundColor: '#FEF9E7',
                                    color: '#F39C12',
                                    border: '1px solid #FCE9B8',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: '#FCE9B8',
                                      borderColor: '#F39C12',
                                    },
                                    '& .MuiChip-icon': {
                                      color: '#F39C12',
                                    },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  {/* No policies message */}
                  {(() => {
                    const managedPolicies = selectedPermissionSet.managed_policies || selectedPermissionSet.managedPolicies || [];
                    const inlinePolicies = selectedPermissionSet.inline_policies || selectedPermissionSet.inlinePolicies || {};
                    const inlinePolicyCount = Object.keys(inlinePolicies).filter(k => inlinePolicies[k] && inlinePolicies[k].trim() !== '').length;
                    if (managedPolicies.length === 0 && inlinePolicyCount === 0) {
                      return (
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic' }}>
                          No policies attached to this permission set
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Paper>
              )}
            </>
          )}

          {/* Custom Permission Set Builder */}
          {useCustomPermissionSet && (
            <Box sx={{ mb: 3 }}>
              {/* Permission Set Name */}
              <TextField
                fullWidth
                label="Permission Set Name"
                placeholder="e.g., CustomS3Access"
                value={customPSName}
                onChange={(e) => {
                  setCustomPSName(e.target.value);
                  if (formErrors.customPSName) setFormErrors(prev => ({ ...prev, customPSName: null }));
                }}
                error={!!formErrors.customPSName}
                helperText={formErrors.customPSName || 'A unique name (will be prefixed with "JIT-")'}
                sx={{ ...inputStyle, mb: 2 }}
              />

              {/* Session Duration */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', '&.Mui-focused': { color: '#4698D3' } }}>
                  Session Duration (AWS Console)
                </InputLabel>
                <Select
                  value={customSessionDuration.value}
                  onChange={(e) => {
                    const duration = sessionDurationOptions.find(d => d.value === e.target.value);
                    setCustomSessionDuration(duration);
                  }}
                  label="Session Duration (AWS Console)"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E5E5' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4698D3' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4698D3' },
                  }}
                >
                  {sessionDurationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* AWS Managed Policies Accordion */}
              <Accordion
                defaultExpanded
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                  sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PolicyIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      AWS Managed Policies ({selectedManagedPolicies.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {/* Selected Policies */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                      Selected Policies
                    </Typography>
                    {selectedManagedPolicies.length === 0 ? (
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                        No policies selected
                      </Typography>
                    ) : (
                      <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 1 }}>
                        {selectedManagedPolicies.map(policyArn => {
                          const policyDetails = awsManagedPolicies.find(p => p.arn === policyArn) || { name: 'Unknown Policy' };
                          return (
                            <Box key={policyArn} sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              px: 1.5,
                              mb: 0.5,
                              backgroundColor: '#EBF5FB',
                              borderRadius: '4px',
                              '&:last-child': { mb: 0 }
                            }}>
                              <Box>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                                  {policyDetails.name}
                                </Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#999999' }}>
                                  {policyArn}
                                </Typography>
                              </Box>
                              <IconButton onClick={() => handleRemoveManagedPolicy(policyArn)} size="small" sx={{ color: '#E74C3C' }}>
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={handleShowPolicyPicker}
                    disabled={isLoadingPolicies}
                    startIcon={isLoadingPolicies ? <CircularProgress size={16} /> : <AddIcon />}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#E5E5E5',
                      color: '#666666',
                      mb: 2,
                      '&:hover': { borderColor: '#4698D3', backgroundColor: '#EBF5FB', color: '#4698D3' },
                    }}
                  >
                    {isLoadingPolicies ? 'Loading Policies...' : showPolicyPicker ? 'Hide Policy Browser' : 'Browse & Add Policies'}
                  </Button>

                  {/* Policy Picker */}
                  {showPolicyPicker && (
                    <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2 }}>
                      {isLoadingPolicies ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={24} sx={{ color: '#4698D3', mb: 2 }} />
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                            Fetching AWS managed policies...
                          </Typography>
                        </Box>
                      ) : awsManagedPolicies.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                            No AWS managed policies available.
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={fetchAwsPolicies}
                            sx={{ mt: 2, fontFamily: 'Metropolis, sans-serif', textTransform: 'none', borderColor: '#E5E5E5' }}
                          >
                            Retry Loading Policies
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              fullWidth
                              placeholder="Search policies by name, description, or ARN..."
                              value={policySearchTerm}
                              onChange={(e) => { setPolicySearchTerm(e.target.value); setCurrentPage(1); }}
                              size="small"
                              sx={{ ...inputStyle, mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Category</InputLabel>
                                <Select
                                  value={selectedPolicyCategory}
                                  onChange={(e) => { setSelectedPolicyCategory(e.target.value); setCurrentPage(1); }}
                                  label="Category"
                                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                                >
                                  <MenuItem value="">All Categories</MenuItem>
                                  {getPolicyCategories().map(category => (
                                    <MenuItem key={category} value={category}>{category}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                                <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Sort</InputLabel>
                                <Select
                                  value={policySortBy}
                                  onChange={(e) => setPolicySortBy(e.target.value)}
                                  label="Sort"
                                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                                >
                                  <MenuItem value="name">Sort: A-Z</MenuItem>
                                  <MenuItem value="nameDesc">Sort: Z-A</MenuItem>
                                  <MenuItem value="service">Sort: Service</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>

                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666666', fontWeight: 500, mb: 1.5 }}>
                            Showing {paginatedPolicies.length} of {filteredPolicies.length} policies
                            {policySearchTerm && ` matching "${policySearchTerm}"`}
                            {selectedPolicyCategory && ` in ${selectedPolicyCategory}`}
                          </Typography>

                          <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E5E5E5', borderRadius: '6px' }}>
                            {paginatedPolicies.map(policy => {
                              const isSelected = selectedManagedPolicies.includes(policy.arn);
                              return (
                                <Box key={policy.arn} sx={{
                                  p: 1.5,
                                  borderBottom: '1px solid #E5E5E5',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  backgroundColor: isSelected ? '#EBF5FB' : '#FFFFFF',
                                  '&:hover': { backgroundColor: isSelected ? '#D6EAF8' : '#F9FBFD' },
                                  '&:last-child': { borderBottom: 'none' },
                                }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                                      {policy.name}
                                    </Typography>
                                    {policy.description && (
                                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', fontWeight: 500 }}>
                                        {policy.description}
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#999999', mt: 0.25 }}>
                                      {policy.arn}
                                    </Typography>
                                  </Box>
                                  <Button
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => isSelected ? handleRemoveManagedPolicy(policy.arn) : handleAddManagedPolicy(policy.arn)}
                                    sx={{
                                      ml: 2,
                                      minWidth: '80px',
                                      fontFamily: 'Metropolis, sans-serif',
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      ...(isSelected ? {
                                        backgroundColor: '#4698D3',
                                        '&:hover': { backgroundColor: '#3580B8' },
                                      } : {
                                        borderColor: '#E5E5E5',
                                        color: '#666666',
                                        '&:hover': { borderColor: '#4698D3', color: '#4698D3' },
                                      }),
                                    }}
                                  >
                                    {isSelected ? 'Selected' : '+ Add'}
                                  </Button>
                                </Box>
                              );
                            })}
                          </Box>

                          {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                              <Button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                              >
                                Previous
                              </Button>
                              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                                Page {currentPage} of {totalPages}
                              </Typography>
                              <Button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                              >
                                Next
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Inline Policies Accordion */}
              <Accordion
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
                  sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CodeIcon sx={{ color: '#F39C12', fontSize: 22 }} />
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      Inline Policies ({countInlinePolicies(inlinePolicies)})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {/* Add New Inline Policy */}
                  <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Policy Name"
                      placeholder="Enter policy name"
                      value={inlinePolicyName}
                      onChange={(e) => setInlinePolicyName(filterInput(e.target.value, 'IAM_POLICY_NAME'))}
                      helperText="Only letters, numbers, and + = , . @ _ - are allowed (no spaces)"
                      inputProps={{ maxLength: 128 }}
                      size="small"
                      sx={{ ...inputStyle, mb: 2 }}
                    />

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                          Policy JSON
                        </Typography>
                        {policyValidation.isValidating && (
                          <Chip
                            label="Validating..."
                            size="small"
                            sx={{ backgroundColor: '#FEF9E7', color: '#F39C12', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                          />
                        )}
                        {!policyValidation.isValidating && policyValidation.result && !policyValidation.error &&
                          !policyValidation.result.findings?.some(f => f.findingType === 'ERROR') && (
                            <Chip
                              label="Valid"
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              sx={{ backgroundColor: '#E8F8F0', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                            />
                          )}
                        <PolicyGeneratorLink sx={{ ml: 'auto' }} />
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={10}
                        placeholder='{"Version": "2012-10-17", "Statement": [...]}'
                        value={inlinePolicyContent}
                        onChange={(e) => setInlinePolicyContent(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderColor: policyValidation.error || (policyValidation.result?.findings?.some(f => f.findingType === 'ERROR'))
                                ? '#E74C3C'
                                : '#E5E5E5',
                            },
                            '&:hover fieldset': { borderColor: '#4698D3' },
                            '&.Mui-focused fieldset': { borderColor: '#4698D3' },
                          },
                        }}
                      />
                      {renderValidationResults()}
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleAddInlinePolicy}
                      disabled={!inlinePolicyName || !inlinePolicyContent || policyValidation.isValidating}
                      startIcon={<AddIcon />}
                      sx={{
                        mt: 2,
                        backgroundColor: '#4698D3',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#3580B8' },
                        '&.Mui-disabled': { backgroundColor: '#E5E5E5', color: '#999999' },
                      }}
                    >
                      Add Policy
                    </Button>
                  </Box>

                  {/* Existing Inline Policies */}
                  {countInlinePolicies(inlinePolicies) > 0 && (
                    <Box>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                        Current Inline Policies ({countInlinePolicies(inlinePolicies)})
                      </Typography>
                      {Object.entries(inlinePolicies).filter(([, content]) => content && content.trim() !== '').map(([name, content]) => (
                        <Box key={name} sx={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '6px',
                          border: '1px solid #E5E5E5',
                          p: 2,
                          mb: 1,
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66', fontWeight: 600 }}>
                              {name}
                            </Typography>
                            <IconButton onClick={() => handleRemoveInlinePolicy(name)} size="small" sx={{ color: '#E74C3C' }}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box sx={{
                            backgroundColor: '#F9FBFD',
                            borderRadius: '4px',
                            p: 1.5,
                            maxHeight: '150px',
                            overflow: 'auto',
                            border: '1px solid #E5E5E5',
                          }}>
                            <Typography
                              component="pre"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                color: '#666666',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                margin: 0
                              }}
                            >
                              {previewPolicyJson(content)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {formErrors.customPolicies && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formErrors.customPolicies}
                </Alert>
              )}
            </Box>
          )}

          {/* Duration Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', '&.Mui-focused': { color: '#4698D3' } }}>
              Access Duration
            </InputLabel>
            <Select
              value={selectedDuration.value}
              onChange={(e) => {
                const duration = durationOptions.find(d => d.value === e.target.value);
                setSelectedDuration(duration);
              }}
              label="Access Duration"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E5E5' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4698D3' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4698D3' },
              }}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TimeIcon sx={{ fontSize: 16, color: '#666666' }} />
              <Typography variant="caption" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                Access will be automatically revoked after {formatDurationDisplay(selectedDuration.value)}
              </Typography>
            </Box>
          </FormControl>

          {/* Justification */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Justification"
            placeholder="Please provide a detailed reason for this access request..."
            value={justification}
            onChange={(e) => {
              setJustification(e.target.value);
              if (formErrors.justification) setFormErrors(prev => ({ ...prev, justification: null }));
            }}
            error={!!formErrors.justification}
            helperText={formErrors.justification}
            sx={{ ...inputStyle, mb: 4 }}
          />

          {/* Request Summary */}
          {selectedAccount && (selectedPermissionSet || (useCustomPermissionSet && customPSName.trim())) && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 4,
                backgroundColor: useCustomPermissionSet ? 'rgba(255, 152, 0, 0.04)' : 'rgba(70, 152, 211, 0.04)',
                border: useCustomPermissionSet ? '1px solid rgba(255, 152, 0, 0.2)' : '1px solid rgba(70, 152, 211, 0.2)',
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', mb: 2 }}
              >
                Request Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Account: ${selectedAccount.name || selectedAccount.account_id}`} size="small" sx={{ fontFamily: 'Metropolis, sans-serif' }} />
                {useCustomPermissionSet ? (
                  <Chip
                    label={`Custom PS: JIT-${customPSName.trim()}`}
                    size="small"
                    sx={{ fontFamily: 'Metropolis, sans-serif', backgroundColor: 'rgba(255, 152, 0, 0.15)', color: '#E65100' }}
                  />
                ) : (
                  <Chip label={`Role: ${selectedPermissionSet.name}`} size="small" sx={{ fontFamily: 'Metropolis, sans-serif' }} />
                )}
                <Chip label={`Duration: ${selectedDuration.label}`} size="small" icon={<TimeIcon />} sx={{ fontFamily: 'Metropolis, sans-serif' }} />
                {useCustomPermissionSet && (
                  <Chip
                    label="Multi-level approval"
                    size="small"
                    sx={{ fontFamily: 'Metropolis, sans-serif', backgroundColor: 'rgba(255, 152, 0, 0.15)', color: '#E65100' }}
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#E5E5E5',
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': { borderColor: '#D9DBDC', backgroundColor: 'rgba(0, 0, 0, 0.02)' },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{
                backgroundColor: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: '#3580B8' },
                '&:disabled': { backgroundColor: 'rgba(70, 152, 211, 0.5)' },
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Box>
        </form>
      </Paper>
      </motion.div>

      {/* Inline Policy Viewer Dialog */}
      <Dialog
        open={viewPolicyDialog.open}
        onClose={handleClosePolicyDialog}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #E5E5E5',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CodeIcon sx={{ color: '#F39C12', fontSize: 24 }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  fontSize: '1.1rem',
                }}
              >
                {viewPolicyDialog.policyName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.8rem',
                  color: '#666666',
                }}
              >
                Inline Policy Document
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClosePolicyDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              p: 2,
              m: 2,
              borderRadius: '8px',
              overflow: 'auto',
              maxHeight: '50vh',
              border: '1px solid #E5E5E5',
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: '"Fira Code", "Consolas", monospace',
                fontSize: '0.85rem',
                color: '#253E66',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {viewPolicyDialog.policyContent}
            </Typography>
          </Box>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E5E5', p: 2 }}>
          <Button
            onClick={handleCopyPolicy}
            startIcon={<CopyIcon />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              color: '#666666',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            Copy to Clipboard
          </Button>
          <Button
            onClick={handleClosePolicyDialog}
            variant="contained"
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#3580B8' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog with Approvers */}
      <Dialog
        open={successDialog.open}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#E8F8F0',
            borderBottom: '1px solid #C8E6C9',
            pb: 2,
          }}
        >
          <CheckCircleIcon sx={{ color: '#27AE60', fontSize: 32 }} />
          <Box>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
                fontSize: '1.1rem',
              }}
            >
              Request Submitted Successfully
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.85rem',
                color: '#666666',
              }}
            >
              {successDialog.isCustomPermissionSet
                ? 'Your custom permission set request requires multi-level approval'
                : 'Your access request is pending approval'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PeopleIcon sx={{ color: '#4698D3', fontSize: 24 }} />
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  fontSize: '1rem',
                }}
              >
                Approvers
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.9rem',
                color: '#666666',
                mb: 2,
              }}
            >
              Your request will be reviewed by the following account owner(s). If you need to follow up,
              you can reach out to them directly:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '8px',
                border: '1px solid #E5E5E5',
                p: 2,
              }}
            >
              {successDialog.approvers.map((email, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: index < successDialog.approvers.length - 1 ? '1px solid #E5E5E5' : 'none',
                  }}
                >
                  <EmailIcon sx={{ color: '#4698D3', fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.9rem',
                      color: '#253E66',
                      fontWeight: 500,
                    }}
                  >
                    {email}
                  </Typography>
                  <Tooltip title="Copy email" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(email);
                        toast.success('Email copied to clipboard');
                      }}
                      sx={{ ml: 'auto', color: '#999999', '&:hover': { color: '#4698D3' } }}
                    >
                      <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
          {successDialog.isCustomPermissionSet && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                backgroundColor: 'rgba(70, 152, 211, 0.08)',
                border: '1px solid rgba(70, 152, 211, 0.2)',
                '& .MuiAlert-icon': { color: '#4698D3' },
              }}
            >
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>
                After account owner approval, an SSO administrator will also need to approve your
                custom permission set request.
              </Typography>
            </Alert>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E5E5', p: 2 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              '&:hover': { backgroundColor: '#3580B8' },
            }}
          >
            View My Requests
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITRequestForm;
